// WhatsNewDrawer — shows a small bottom-sheet on first visit after a new
// release. Users see it once per changelog version (localStorage flag).
import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Sparkles, ArrowRight, X } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";

// Bump this whenever there are notable new features to announce.
export const CURRENT_CHANGELOG_VERSION = "2026-07-26";
const KEY = "rostr.whatsnew_seen";

const ITEMS = [
  { emoji: "☕", titleKey: "wn_auto_pause_title", descKey: "wn_auto_pause_desc" },
  { emoji: "🎯", titleKey: "wn_dnd_title", descKey: "wn_dnd_desc" },
  { emoji: "📱", titleKey: "wn_mobile_roster_title", descKey: "wn_mobile_roster_desc" },
  { emoji: "🪟", titleKey: "wn_widget_popup_title", descKey: "wn_widget_popup_desc" },
];

export default function WhatsNewDrawer({ manuallyOpen, onClose } = {}) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);

  // Auto-open only if user is truly new (never seen ANY changelog).
  // Otherwise the drawer stays hidden until the header "What's new" button opens it.
  useEffect(() => {
    try {
      const seen = localStorage.getItem(KEY);
      if (!seen) {
        const timer = setTimeout(() => setOpen(true), 1500);
        return () => clearTimeout(timer);
      }
    } catch { /* ignore */ }
    // Listen for global "show what's new" event fired from the header sparkles button.
    const handler = () => setOpen(true);
    window.addEventListener("rostr:show-whats-new", handler);
    return () => window.removeEventListener("rostr:show-whats-new", handler);
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(KEY, CURRENT_CHANGELOG_VERSION); } catch {}
    setOpen(false);
    if (onClose) onClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ y: 200, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 200, opacity: 0 }}
          transition={{ type: "spring", stiffness: 260, damping: 28 }}
          className="fixed bottom-6 left-6 md:left-auto md:right-6 z-[75] w-[calc(100%-3rem)] md:w-[400px] rounded-3xl overflow-hidden"
          style={{
            background: "linear-gradient(135deg, hsl(var(--surface)) 0%, hsl(var(--primary-soft)) 100%)",
            border: "1px solid hsl(var(--border))",
            boxShadow: "0 30px 80px -20px rgb(0 0 0 / 0.35), 0 8px 22px -8px rgb(0 0 0 / 0.14)",
          }}
          data-testid="whatsnew-drawer"
        >
          <div className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <div
                className="w-8 h-8 rounded-xl grid place-items-center text-white"
                style={{ background: "linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(217 91% 45%) 100%)" }}
              >
                <Sparkles size={15} strokeWidth={2} />
              </div>
              <div className="flex-1">
                <div className="eyebrow">{t("wn_eyebrow")}</div>
                <div className="font-display text-[17px] font-bold text-[hsl(var(--text-strong))] leading-tight">{t("wn_title")}</div>
              </div>
              <button onClick={dismiss} className="w-8 h-8 grid place-items-center rounded-full hover:bg-white/60" aria-label={t("close")} data-testid="whatsnew-close">
                <X size={14} />
              </button>
            </div>
            <ul className="space-y-2.5 mt-4">
              {ITEMS.map((it, i) => (
                <li key={i} className="flex items-start gap-3 rounded-2xl bg-white/70 backdrop-blur-sm p-3">
                  <div className="text-[22px] leading-none shrink-0 mt-0.5">{it.emoji}</div>
                  <div className="min-w-0">
                    <div className="text-[13.5px] font-bold text-[hsl(var(--text-strong))] leading-tight">{t(it.titleKey)}</div>
                    <div className="text-[11.5px] text-[hsl(var(--text-muted))] mt-0.5 leading-snug">{t(it.descKey)}</div>
                  </div>
                </li>
              ))}
            </ul>
            <button
              onClick={dismiss}
              data-testid="whatsnew-dismiss"
              className="mt-5 w-full h-11 rounded-full font-semibold text-[14px] text-white flex items-center justify-center gap-2 group"
              style={{ background: "linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(217 91% 45%) 100%)", boxShadow: "0 12px 30px -8px hsl(var(--primary) / 0.5)" }}
            >
              {t("wn_got_it")}
              <ArrowRight size={15} className="group-hover:translate-x-0.5 transition-transform" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
